import * as Sentry from "@sentry/browser";
import classNames from "classnames";
import { APP_MANIFEST_V3 } from "constant/application";
import * as AppVersionType from "fleet/constant/app-version-type";
import { captureStack } from "integrations/sentry";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import PropTypes from "prop-types";
import React from "react";
import { Alert, Button, Col, Row } from "react-bootstrap";
import { PageAlert } from "ui/alert/page-alert";
import { BackButton } from "ui/button/back";
import { AppCheckboxGroup } from "ui/checkbox/app-checkbox-group";
import { Fetch } from "ui/fetch";
import { List } from "ui/list";
import { Notification } from "ui/notification";
import { AppParameterFormLayout } from "ui/parameter-form/app-param-form-layout";
import { CustomParameterForm } from "ui/parameter-form/custom-parameter-form";
import { PRIORITY } from "ui/parameter-form/utils";
import { devLogger } from "util/logging";
import { difference } from "util/set";
import { sortByProp } from "util/sort-by-prop";

const sortByAppDisplayNameAsc = sortByProp.bind(
  null,
  "app.display_name",
  "asc",
);

class ConfigureParams extends React.PureComponent {
  static get propTypes() {
    return {
      addErrorMessage: PropTypes.func.isRequired,
      apps: PropTypes.array.isRequired,
      deviceParams: PropTypes.array,
      network: PropTypes.object.isRequired,
      onSubmit: PropTypes.func.isRequired,
      priority: PropTypes.oneOf([PRIORITY.DEVICE, PRIORITY.NETWORK]).isRequired,
      readonly: PropTypes.bool.isRequired,
      showTestingApps: PropTypes.bool,
      successLink: PropTypes.node.isRequired,
      systemManagedParams: PropTypes.array,
    };
  }

  static get defaultProps() {
    return {
      deviceParams: [],
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      fieldErrors: {},
      nonFieldErrors: [],
      selectedApps: new Set(),
      pristineApps: new Set(),
      submitted: false,
      successMessage: "",
      priorityParams: [], // Sentinel.
    };

    this._appForms = {};
    this._isMounted = true;

    this._onClickAppIcon = this._onClickAppIcon.bind(this);
    this._removeAppParamForm = this._removeAppParamForm.bind(this);
    this._submit = this._submit.bind(this);
    this._updateParams = this._updateParams.bind(this);
  }

  componentDidMount() {
    this._initParams();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    this._initParams();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  registerAppForm(appId, form) {
    this._appForms[appId] = form;
  }

  _getFormValues(formValues) {
    return Object.entries(formValues).reduce((acc, [appId, getFormValues]) => {
      acc[appId] = getFormValues();
      return acc;
    }, {});
  }

  _collectAppFormState(appForms) {
    const formValues = {};
    const promises = [];

    Object.entries(appForms).forEach(([appId, getFormApi]) => {
      const { form, getFormValues } = getFormApi();
      const promise = form.submitForm();

      formValues[appId] = getFormValues;
      promises.push(promise);
    });

    return Promise.all(promises)
      .then(() => this._getFormValues(formValues))
      .catch(() => {
        const errorMessage = "Please address form errors.";
        const error = new Error(errorMessage);
        error.data = {
          field_errors: {},
          non_field_errors: [errorMessage],
        };

        return Promise.reject(error);
      });
  }

  _getNetworkParams() {
    const { network } = this.props;

    return get(network, "user_params", []);
  }

  _getRestrictedAppVersions(apps, restrictions, showTesting) {
    const { priority, network } = this.props;
    if (priority === PRIORITY.DEVICE && showTesting) {
      return {};
    }

    return restrictions.reduce((acc, restriction) => {
      const app = apps.find((app) => app.id === restriction.app_id);
      if (!app) {
        acc[restriction.app_id] = null;
        this._appNotFoundWarning(restriction.app_id, network.id);

        return acc;
      }

      const appVersion = app.versions.find(
        (v) => v.id === restriction.version_id,
      );
      acc[app.id] = appVersion || null;

      if (!appVersion) {
        this._appVersionNotFoundWarning(
          app.id,
          restriction.version_id,
          network.id,
        );
      }

      return acc;
    }, {});
  }

  _getAppParamSchemas(apps, { showTesting }, restrictedAppVersions) {
    const appType = showTesting
      ? AppVersionType.TESTING
      : AppVersionType.STABLE;

    return apps
      .map((app) => {
        const version = get(restrictedAppVersions, app.id, app[appType]);

        return [app, version];
      })
      .filter(([_app, version]) => {
        return version && version.manifest_version === APP_MANIFEST_V3;
      })
      .map(([app, version]) => {
        return {
          app,
          schemas: version.user_params,
          version,
        };
      })
      .sort(sortByAppDisplayNameAsc);
  }

  _getPriorityParams() {
    const { deviceParams, priority } = this.props;

    if (priority === PRIORITY.DEVICE) {
      return deviceParams;
    }

    if (priority === PRIORITY.NETWORK) {
      return this._getNetworkParams();
    }

    throw new Error("Unhandled priority.");
  }

  _appNotFoundWarning(appId, networkId) {
    const warning =
      "Restricted app not found. " +
      `Network Id: ${networkId}, App Id: ${appId}`;

    devLogger(console.warn, warning);
    captureStack();
    Sentry.captureMessage(warning, Sentry.Severity.Warning);
  }

  _appVersionNotFoundWarning(appId, appVersionId, networkId) {
    const warning =
      `App version not found. Network Id: ${networkId}, ` +
      `App Id: ${appId}, Version Id: ${appVersionId}`;

    devLogger(console.warn, warning);
    captureStack();
    Sentry.captureMessage(warning, Sentry.Severity.Warning);
  }

  _initParams() {
    const priorityParams = this._getPriorityParams();
    const { priorityParams: priorityParamsState, selectedApps: currentApps } =
      this.state;

    // If we have user params then that means at this level (device, network)
    // we have values to populate on the form, so we must show the form.
    const paramsChanged = !isEqual(priorityParamsState, priorityParams);
    if (paramsChanged) {
      this.setState({ priorityParams });

      // We only need to recompute this if user params changed.
      const selectedApps = new Set([
        ...currentApps,
        ...priorityParams.filter((p) => p.app_id).map((p) => p.app_id),
      ]);

      // Show the form if we have device level params set.
      if (!isEqual(selectedApps, currentApps)) {
        this.setState({
          selectedApps,
          pristineApps: new Set(selectedApps),
        });
      }
    }
  }

  _onClickAppIcon(e) {
    const { checked, dataset } = e.target;
    const { id } = dataset;
    const appId = Number(id);

    if (checked) {
      this.setState({
        selectedApps: new Set(this.state.selectedApps).add(appId),
      });
    } else {
      this._removeAppParamForm(appId);
    }
  }

  _removeAppParamForm(appId) {
    const newState = new Set(this.state.selectedApps);
    newState.delete(appId);

    delete this._appForms[appId];

    this.setState({
      selectedApps: newState,
    });
  }

  _submit() {
    this.setState(
      {
        fieldErrors: {},
        nonFieldErrors: [],
        submitted: true,
        successMessage: "",
      },
      () => {
        this._updateParams()
          .catch((e) => {
            if (!this._isMounted) return;

            this.setState({
              fieldErrors: get(e, "data.field_errors") || {},
              nonFieldErrors: get(e, "data.non_field_errors") || [],
            });

            const message = get(e, "message");
            if (message) {
              this.props.addErrorMessage(
                "Sorry, there was an error submitting your request: " + message,
              );
              return Promise.reject(e);
            }
          })
          .finally(() => {
            if (!this._isMounted) return;

            this.setState({ submitted: false });
          });
      },
    );
  }

  _filterEmptyValues([name, value]) {
    if (value === undefined) {
      return false;
    }

    if (typeof value === "string" && !value) {
      return false;
    }

    return true;
  }

  _transformAppFormState(formValues) {
    return Object.entries(formValues).reduce((acc, [appId, values]) => {
      const appParams = Object.entries(values)
        .filter(this._filterEmptyValues)
        .map(([name, value]) => {
          return {
            name,
            value,
            appId: Number(appId),
          };
        });

      acc = acc.concat(appParams);

      return acc;
    }, []);
  }

  _transformCustomParamState(params) {
    return params.map(({ k, v }) => ({ name: k, value: v }));
  }

  _updateParams() {
    const { onSubmit } = this.props;
    const rawCustomParams = this.form.getParams();
    const customParams = this._transformCustomParamState(rawCustomParams);

    return this._collectAppFormState(this._appForms).then((formValues) => {
      const appParams = this._transformAppFormState(formValues);

      const params = [...customParams, ...appParams];
      return onSubmit(params);
    });
  }

  _renderV3Apps() {
    const {
      apps,
      deviceParams,
      network,
      priority,
      readonly,
      systemManagedParams,
      showTestingApps: showTesting,
    } = this.props;
    const { fieldErrors, selectedApps, pristineApps } = this.state;
    const appsChanged = difference(pristineApps, selectedApps).size > 0;
    const restrictedAppVersions = this._getRestrictedAppVersions(
      apps,
      network.app_restrictions,
      showTesting,
    );

    const v3Apps = this._getAppParamSchemas(
      apps,
      { showTesting },
      restrictedAppVersions,
    );

    if (v3Apps.length === 0) {
      return (
        <Row>
          <Col xs={12}>
            <p className="no-v3-apps">No applications available.</p>
          </Col>
        </Row>
      );
    }

    const networkParams = this._getNetworkParams();

    return (
      <div>
        <Row>
          <Col xs={12} className="mb-4">
            <strong>Select the application(s) to edit parameters</strong>
          </Col>
          <Col xs={12}>
            <AppCheckboxGroup
              apps={v3Apps.map((a) => a.app)}
              onChange={this._onClickAppIcon}
              selectedApps={selectedApps}
            />
          </Col>
          <Col xs={12}>
            {appsChanged && (
              <Alert variant="info">
                <i className="fa fa-info-circle" aria-hidden="true" />
                You&apos;re about to delete certain application parameters.
                Please make sure all relevant applications are selected above,
                prior to submitting this form.
              </Alert>
            )}
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            {v3Apps
              .filter(({ app }) => selectedApps.has(app.id))
              .map(({ app, schemas, version }) => {
                return (
                  <AppParameterFormLayout
                    key={app.id}
                    app={app}
                    deviceParams={deviceParams.filter(
                      (p) => p.app_id === app.id,
                    )}
                    formErrors={fieldErrors}
                    getFormApi={this.registerAppForm.bind(this, app.id)}
                    priority={priority}
                    network={network}
                    networkParams={networkParams.filter(
                      (p) => p.app_id === app.id,
                    )}
                    onRemove={this._removeAppParamForm}
                    readonly={readonly}
                    schemas={schemas}
                    systemManagedParams={systemManagedParams}
                    version={version}
                  />
                );
              })}
          </Col>
        </Row>
      </div>
    );
  }

  render() {
    const { deviceParams, network, priority, successLink, readonly } =
      this.props;
    const { nonFieldErrors, submitted, successMessage } = this.state;
    const networkParams = this._getNetworkParams();
    const devCustomParams = deviceParams.filter((p) => !p.app_id);
    const netCustomParams = networkParams.filter((p) => !p.app_id);

    const errList = nonFieldErrors.length ? (
      <List className="list-compact" items={nonFieldErrors} />
    ) : null;

    const hasAppRestrictions = Boolean(network.app_restrictions.length);

    return (
      <div>
        <Row>
          <Col xs={12}>
            <Notification
              error={errList}
              success={successMessage}
              successChildren={successLink}
            />
            {hasAppRestrictions && (
              <PageAlert>
                This network contains an application restriction. Devices will
                use the version indicated by the restriction.
              </PageAlert>
            )}
            <div className="v3-app-forms">
              <h2 className="subtitle">Application Parameters</h2>
              {this._renderV3Apps()}
            </div>
            <Row>
              <Col xs={12}>
                <h2 className="subtitle">Custom Parameters</h2>
                <Fetch.LegacyParams injectAs="legacyParams">
                  <CustomParameterForm
                    deviceParams={devCustomParams}
                    network={network}
                    networkParams={netCustomParams}
                    readOnly={readonly}
                    ref={(f) => {
                      this.form = f;
                    }}
                    priority={priority}
                  />
                </Fetch.LegacyParams>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col xs={12} className="button-spacing text-right mt-25">
            {/*
              TODO(etsai): cor-2480 Make buttons sticky to the bottom of page
                           and do upload
            */}
            <BackButton />
            <Button
              variant="primary"
              onClick={this._submit}
              className={classNames({ hide: readonly })}
              disabled={submitted}
            >
              Update
            </Button>
          </Col>
        </Row>
      </div>
    );
  }
}

export { ConfigureParams };
